import React from 'react';
import {
  FormProvider,
  useForm,
  type FieldValues,
  type UseFormProps,
  type UseFormReturn,
} from 'react-hook-form';
import DialogContent from '@mui/material/DialogContent';
import {
  DialogActionButton,
  DialogActions,
  DialogCancelButton,
  DialogHeader,
} from '@/components/common/CustomDialog';
import useStatefulId from '@/lib/hooks/useStatefulId';
import translate from '@/lib/intl/translate';

export type FormDialogContentProps<FormValues extends FieldValues> = {
  children:
    | React.ReactNode
    | ((methods: UseFormReturn<FormValues>) => React.ReactNode);
  formId?: string;
  onClose: () => void;
  headerText: string;
  hookFormProps: UseFormProps<FormValues>;
  onSubmit: (formData: FormValues) => void;
  submitLabel?: string;
};

export default function FormDialogContent<FormValues extends FieldValues>({
  children,
  formId,
  headerText,
  hookFormProps,
  onClose,
  onSubmit,
  submitLabel = translate('SUBMIT'),
}: FormDialogContentProps<FormValues>) {
  const id = useStatefulId(formId);
  const formMethods = useForm<FormValues>(hookFormProps);
  const { formState, handleSubmit, reset } = formMethods;
  const { defaultValues } = hookFormProps;

  React.useEffect(() => {
    reset(defaultValues);
  }, [defaultValues]);

  const handleFormSubmit = React.useCallback(
    async (formData: FormValues) => onSubmit(formData),
    [onSubmit],
  );

  return (
    <FormProvider {...formMethods}>
      <DialogHeader headerText={headerText} onClose={onClose} />

      <DialogContent className="hide-scrollbar" dividers>
        <form id={id} noValidate onSubmit={handleSubmit(handleFormSubmit)}>
          {children instanceof Function ? children(formMethods) : children}
        </form>
      </DialogContent>
      <DialogActions>
        <DialogActionButton
          form={id}
          id={`${id}-submit-btn`}
          showLoader={formState.isSubmitting}
          type="submit"
        >
          {submitLabel}
        </DialogActionButton>
        <DialogCancelButton id={`${id}-cancel-btn`} onClick={onClose} />
      </DialogActions>
    </FormProvider>
  );
}
